import React, { useMemo } from 'react';
import { useIntl, useParams } from 'umi';
import { JumpType, navigate } from '@home/utils/utils';
import SvgIcon from '../SvgIcon';
import { dynamicString } from '@home/utils/dynamicString';
import { LANGUAGE_EN } from '@home/_config/config';
import { getName } from '@home/utils/common';
import './index.scss';
interface IMetricsCardLinkProps {
  /** 链接地址，支持完整 URL 或相对业务路径 */
  url?: string;
  link?: string;
  /** 英文文案 */
  textEn?: string;
  /** 中文文案 */
  textCn?: string;
  /** 跳转方式，默认新开窗口 */
  jumpType?: JumpType;
  /** 自定义左侧图标 */
  prefixIcon: string;
  /** 自定义类名 */
  className?: string;
  /** 点击拦截（返回 true 则不继续默认跳转） */
  beforeNavigate?: () => boolean | void;
}

/**
 * MetricsCardLink 简单的可点击链接组件，支持新开窗口或历史栈跳转。
 * 支持国际化文本显示，自定义图标，以及不同的跳转方式
 */
const MetricsCardLink: React.FC<IMetricsCardLinkProps> = (props) => {
  // 从props中解构获取组件配置参数
  const {
    url,           // 跳转链接URL
    link,          // 备用跳转链接
    textEn = 'Dashboard',  // 英文文本默认值
    textCn = 'Dashboard',  // 中文文本默认值
    jumpType = JumpType.Window,  // 跳转类型默认值
    prefixIcon = 'dashboard-link',  // 前缀图标默认值
    className = '',  // 自定义类名
    beforeNavigate,  // 跳转前的回调函数
  } = props;
  // 获取URL参数和国际化配置
  const urlParams = useParams();
  const { locale } = useIntl();
  // 判断是否为英文环境
  const isEn = locale === LANGUAGE_EN;

  // 使用useMemo优化文本渲染，根据当前语言环境选择显示文本
  const { text } = useMemo(() => {
    const text = getName(isEn, textEn, textCn);
    return { text };
  }, [isEn, textCn, textEn]);

  // 处理点击事件
  const handleClick = (): void => {
    // 允许外部拦截跳转
    const intercepted = beforeNavigate?.();
    if (intercepted === true) return;
    const jumpUrl = url || link;  // 优先使用url，其次使用link
    if (!jumpUrl) return;
    // 模板字符串取路由参数
    navigate(dynamicString(jumpUrl, urlParams), { jumpType });
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={`dbh-metrics-card-link ${className}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      aria-label={text}
    >
      {prefixIcon && <SvgIcon className='dbh-metrics-card-link-prefix-icon' type={prefixIcon} />}
      <span className="dbh-metrics-card-link-text">{text}</span>
      <SvgIcon className='dbh-metrics-card-link-arrow' type='arrow-right' />
    </div>
  );
};

export default React.memo(MetricsCardLink);


